import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { taskRequestApi } from '../services/taskRequestApi';

interface TaskCommentsProps {
  taskId: number;
}

export default function TaskComments({ taskId }: Readonly<TaskCommentsProps>) {
  const queryClient = useQueryClient();
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: comments, isLoading } = useQuery({
    queryKey: ['task-request-comments', taskId],
    queryFn: () => taskRequestApi.getTaskRequestComments(taskId),
    enabled: taskId > 0,
  });

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const handleSubmit = async () => {
    if (!commentText.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await taskRequestApi.addTaskRequestComment(taskId, commentText.trim());
      setCommentText('');
      queryClient.invalidateQueries({ queryKey: ['task-request-comments', taskId] });
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="task-comments-section">
      <h3>Comments</h3>

      {isLoading ? (
        <p className="task-comments-empty">Loading comments...</p>
      ) : (comments ?? []).length === 0 ? (
        <p className="task-comments-empty-spaced">
          No comments yet.
        </p>
      ) : (
        <div className="task-comments-list">
          {(comments ?? []).map((comment) => (
            <div
              key={comment.id}
              className={`task-comment ${
                comment.commentType !== 'UserComment' ? 'task-comment-system' : ''
              }`}
            >
              <div className="task-comment-header">
                <span className="task-comment-author">
                  {comment.createdByUserName ?? 'System'}
                </span>
                <span className="task-comment-date">
                  {formatDateTime(comment.createdAt)}
                </span>
              </div>
              <div className="task-comment-text">{comment.commentText}</div>
            </div>
          ))}
        </div>
      )}

      <div className="task-comment-form">
        <textarea
          className="task-comment-input"
          placeholder="Add a comment... (Ctrl+Enter to post)"
          aria-label="Add a comment"
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
        />
        <button
          type="button"
          className="task-comment-submit"
          disabled={!commentText.trim() || isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting ? 'Posting...' : 'Post'}
        </button>
      </div>
    </div>
  );
}
